import React, { useEffect, useState } from "react";
import { Download, X, Share, PlusSquare, Smartphone } from "lucide-react";
import { useCountryConfig } from "../lib/useCountryConfig";

/**
 * PWA install banner for Android/desktop Chrome (`beforeinstallprompt`).
 * iOS Safari does not fire this event — handled separately by showing the
 * Apple-specific share/Add-to-Home-Screen tip.
 */
const STORAGE_KEY = "rr_install_dismissed_v1";

const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !/CriOS|FxiOS/.test(navigator.userAgent);

const isStandalone = () =>
  (typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(display-mode: standalone)").matches) ||
  // iOS Safari quirk
  (typeof navigator !== "undefined" && navigator.standalone === true);

export const InstallBanner = () => {
  const cfg = useCountryConfig();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosTip, setIosTip] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(STORAGE_KEY) === "1") return;

    if (isIOS()) {
      Promise.resolve().then(() => {
        setIosTip(true);
        setShow(true);
      });
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } finally {
      setDeferred(null);
      dismiss();
    }
  };

  if (!show) return null;

  return (
    <div
      className="relative bg-gradient-to-r from-rescue-ink to-[#171c2b] text-white px-4 py-3.5 border-b-2 border-mali-yellow shadow-lg"
      data-testid="install-banner"
    >
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-11 h-11 rounded-2xl bg-mali-yellow/15 border border-mali-yellow/40 flex items-center justify-center">
          <Smartphone className="w-5 h-5 text-mali-yellow" strokeWidth={2.5} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-[0.2em]" data-testid="install-banner-brand">
            Installer {(cfg.brand_name || "").toUpperCase()}
          </div>
          <p className="text-[12px] opacity-90 mt-1 leading-relaxed">
            {iosTip
              ? "Accédez au SOS en 1 tap depuis votre écran verrouillé, même hors-ligne."
              : "Ajoutez l'app à votre écran d'accueil pour un accès SOS instantané, même hors-ligne."}
          </p>

          {iosTip ? (
            <div className="mt-2.5 flex items-center gap-2 flex-wrap" data-testid="install-banner-ios-steps">
              <span className="inline-flex items-center gap-1.5 bg-white/10 rounded-full px-2.5 py-1 text-[10px] font-bold">
                <Share className="w-3.5 h-3.5 text-mali-yellow" strokeWidth={2.5} /> Partager
              </span>
              <span className="text-white/40 text-xs">→</span>
              <span className="inline-flex items-center gap-1.5 bg-white/10 rounded-full px-2.5 py-1 text-[10px] font-bold">
                <PlusSquare className="w-3.5 h-3.5 text-mali-yellow" strokeWidth={2.5} /> Sur l&apos;écran d&apos;accueil
              </span>
            </div>
          ) : (
            <button
              onClick={install}
              data-testid="install-btn"
              className="mt-2.5 inline-flex items-center gap-2 bg-mali-yellow text-rescue-ink px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-[0.15em] shadow-md hover:bg-white active:scale-[0.98] transition-all"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={2.5} /> Installer maintenant
            </button>
          )}
        </div>
        <button
          onClick={dismiss}
          data-testid="install-dismiss"
          aria-label="Fermer"
          className="p-1.5 -m-1 rounded-full hover:bg-white/10 transition-colors shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
